'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SkillAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (results: AssessmentResults) => void;
}

interface AssessmentResults {
  level: string;
  recommendedPrograms: string[];
  score: number;
}

interface UserDetails {
  name: string;
  email: string;
  cellNumber: string;
}

interface AssessmentSubmissionPayload {
  userDetails: UserDetails;
  answers: Record<number, string>;
}

export default function SkillAssessmentModal({ isOpen, onClose, onComplete }: SkillAssessmentModalProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isCollectingDetails, setIsCollectingDetails] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails>({ name: '', email: '', cellNumber: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (isHydrated && typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (isHydrated && typeof document !== 'undefined') {
        document.body.style.overflow = 'unset';
      }
    }

    return () => {
      if (isHydrated && typeof document !== 'undefined') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, isHydrated]);

  const validateUserDetails = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!userDetails.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!userDetails.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!userDetails.cellNumber.trim()) {
      newErrors.cellNumber = 'Phone number is required';
    } else if (!/^\d{10,}$/.test(userDetails.cellNumber.replace(/\D/g, ''))) {
      newErrors.cellNumber = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitAssessment = async () => {
    if (!validateUserDetails()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: AssessmentSubmissionPayload = {
        userDetails,
        answers 
      };

      await submitAssessmentData(payload);
      handleClose();
    } catch (error) {
      console.error('Failed to submit assessment:', error);
      setErrors({ submit: 'Failed to submit assessment. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAssessmentData = async (payload: AssessmentSubmissionPayload): Promise<void> => {
    console.log('payload', payload)
    // const response = await fetch('/api/submit.php', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify(payload),
    // });

    // if (!response.ok) {
    //   throw new Error(`API call failed with status ${response.status}`);
    // }

    // const data = await response.json();
    // return data;
  };

  const questions = [
  {
    "question": "What is your preferred way to learn new concepts?",
    "options": [
      "Visual (Videos and diagrams)",
      "Kinaesthetic (Learning by doing)",
      "Read/Write (Documentation and notes)",
      "Auditory (Lectures and discussion)"
    ]
  },
  {
    "question": "What is your ultimate objective for taking a course?",
    "options": [
      "Earning a recognized certification",
      "Building a professional portfolio",
      "Personal hobby or self-improvement",
      "Preparing for competitive exams"
    ]
  },
  {
    "question": "How quickly do you want to see results?",
    "options": [
      "Rapidly (Intensive 1-3 month programs)",
      "Steadily (Flexible 6-month programs)",
      "Deeply (Long-term 12+ month mastery)",
      "Whenever (I have no set deadline)"
    ]
  },
  {
    "question": "What is your biggest challenge when learning something new?",
    "options": [
      "Staying motivated and consistent",
      "Finding high-quality study materials",
      "Understanding complex technical concepts",
      "Finding time in a busy schedule"
    ]
  },
  {
    "question": "Which environment helps you perform best?",
    "options": [
      "Competitive (Ranking and leaderboards)",
      "Collaborative (Group projects and forums)",
      "Private (One-on-one focused attention)",
      "Autonomous (Self-paced and independent)"
    ]
  },
  {
    "question": "How do you prefer to receive feedback on your work?",
    "options": [
      "Automated instant grading",
      "Detailed written reviews by experts",
      "Live 1-on-1 video calls",
      "Peer review from other students"
    ]
  },
  {
    "question": "What is your budget range for professional coaching?",
    "options": [
      "Free or budget-friendly resources",
      "Moderate (Monthly subscription model)",
      "Premium (One-time payment for full access)",
      "Investment (High-tier mentorship programs)"
    ]
  },
  {
    "question": "How much prior exposure do you have to this industry?",
    "options": [
      "Absolutely none - I'm a blank slate",
      "A little - I've done some light research",
      "Moderate - I have some related skills",
      "High - I'm looking to specialize further"
    ]
  }
];

  const handleAnswer = (answer: string) => {
    if (!isHydrated) return;

    const newAnswers = { ...answers, [currentStep]: answer };
    setAnswers(newAnswers);

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Move to user details collection
      setIsCollectingDetails(true);
    }
  };

  const calculateResults = (userAnswers: Record<number, string>): AssessmentResults => {
    const experienceLevel = userAnswers[0] || '';
    let level = 'Beginner';
    let score = 0;

    if (experienceLevel.includes('Advanced')) {
      level = 'Advanced';
      score = 85;
    } else if (experienceLevel.includes('Intermediate')) {
      level = 'Intermediate';
      score = 65;
    } else if (experienceLevel.includes('Basic')) {
      level = 'Beginner';
      score = 45;
    } else {
      level = 'Beginner';
      score = 25;
    }

    const interest = userAnswers[3] || '';
    const recommendedPrograms = [];

    if (interest.includes('Web Development')) {
      recommendedPrograms.push('Full Stack Web Development', 'Frontend Mastery');
    } else if (interest.includes('Data Science')) {
      recommendedPrograms.push('Data Science Bootcamp', 'Machine Learning Fundamentals');
    } else if (interest.includes('Mobile')) {
      recommendedPrograms.push('Mobile App Development', 'React Native Mastery');
    } else {
      recommendedPrograms.push('Cloud Architecture', 'DevOps Engineering');
    }

    return { level, recommendedPrograms, score };
  };

  const handleClose = () => {
    if (!isHydrated) return;
    setCurrentStep(0);
    setAnswers({});
    setIsCollectingDetails(false);
    setUserDetails({ name: '', email: '', cellNumber: '' });
    setErrors({});
    onClose();
  };

  const handlePrevious = () => {
    if (!isHydrated) return;
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  const progress = isCollectingDetails ? 100 : ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50">
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-sky-lg overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-muted">
          <div
            className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
            style={{ width: isHydrated ? `${progress}%` : '0%' }}
          />
        </div>

        <div className="p-6 md:p-8">
          {!isCollectingDetails ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="AcademicCapIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-headline font-bold text-foreground">Skill Assessment</h2>
                    <p className="text-sm font-body text-muted-foreground">
                      Question {isHydrated ? currentStep + 1 : 1} of {questions.length}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                  aria-label="Close assessment"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>

              <div className="mb-8">
                <h3 className="text-lg font-headline font-semibold text-foreground mb-6">
                  {questions[currentStep].question}
                </h3>
                <div className="space-y-3">
                  {questions[currentStep].options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswer(option)}
                      className="w-full p-4 text-left bg-surface hover:bg-primary/10 border border-border hover:border-primary rounded-lg transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full border-2 border-border group-hover:border-primary flex items-center justify-center transition-colors duration-200">
                          <div className="w-3 h-3 rounded-full bg-transparent group-hover:bg-primary transition-colors duration-200" />
                        </div>
                        <span className="text-sm font-body text-foreground group-hover:text-primary transition-colors duration-200">
                          {option}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {isHydrated && currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-body font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                  Previous Question
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="UserIcon" size={24} variant="outline" className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-headline font-bold text-foreground">Your Details</h2>
                    <p className="text-sm font-body text-muted-foreground">
                      One last step to complete your assessment
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                  aria-label="Close assessment"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-body font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={userDetails.name}
                    onChange={(e) => {
                      setUserDetails({ ...userDetails, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: '' });
                    }}
                    placeholder="Enter your full name"
                    className={`w-full px-4 py-2 border rounded-lg font-body text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.name ? 'border-red-500' : 'border-border'
                    }`}
                  />
                  {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-body font-medium text-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={userDetails.email}
                    onChange={(e) => {
                      setUserDetails({ ...userDetails, email: e.target.value });
                      if (errors.email) setErrors({ ...errors, email: '' });
                    }}
                    placeholder="Enter your email address"
                    className={`w-full px-4 py-2 border rounded-lg font-body text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.email ? 'border-red-500' : 'border-border'
                    }`}
                  />
                  {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="cellNumber" className="block text-sm font-body font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    id="cellNumber"
                    type="tel"
                    value={userDetails.cellNumber}
                    onChange={(e) => {
                      setUserDetails({ ...userDetails, cellNumber: e.target.value });
                      if (errors.cellNumber) setErrors({ ...errors, cellNumber: '' });
                    }}
                    placeholder="Enter your phone number"
                    className={`w-full px-4 py-2 border rounded-lg font-body text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.cellNumber ? 'border-red-500' : 'border-border'
                    }`}
                  />
                  {errors.cellNumber && <p className="text-sm text-red-500 mt-1">{errors.cellNumber}</p>}
                </div>

                {errors.submit && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{errors.submit}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setIsCollectingDetails(false)}
                  className="flex-1 px-4 py-2 border border-border text-foreground hover:bg-muted rounded-lg transition-colors duration-200 font-body font-medium"
                >
                  Back
                </button>
                <button
                  onClick={submitAssessment}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200 font-body font-medium"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}